import type { ReadinessBlocker, ReadinessState } from "./types";

/**
 * The readiness ladder, lowest rung first, shared by the server and the React
 * layer.
 *
 * Kept in its own module because `readiness-override` reaches `node:crypto`
 * through `governance-content` and the governance panel runs in a browser, so
 * neither can import the other's. A second copy of the order is what lets the
 * dialog count one set of bypassed requirements while a different set is
 * frozen into the decision.
 */
export const READINESS_RUNGS = [
  "Incomplete",
  "Structurally complete",
  "Ready for internal review",
  "Ready for approval",
  "Ready for final export",
  "Exported and locked",
] as const satisfies readonly ReadinessState[];

/** Each rung's height, derived from {@link READINESS_RUNGS} so the order is written once. */
export const READINESS_RANK = Object.fromEntries(
  READINESS_RUNGS.map((rung, index) => [rung, index]),
) as Record<ReadinessState, number>;

/** Blockers holding a programme back from reaching `gate`, or any rung below it. */
export function blockersForGate<T extends Pick<ReadinessBlocker, "gates">>(
  blockers: readonly T[],
  gate: ReadinessState,
): T[] {
  return blockers.filter((blocker) => READINESS_RANK[blocker.gates] <= READINESS_RANK[gate]);
}

/** Whether a derived readiness state has reached `gate`. */
export function meetsReadinessGate(state: ReadinessState, gate: ReadinessState): boolean {
  return READINESS_RANK[state] >= READINESS_RANK[gate];
}

/**
 * Why a gate refused, counting only the blockers that gate checks.
 *
 * Every blocker would include those held for a later rung, which the panel
 * lists apart and the gate does not wait on.
 */
export function readinessRequiredMessage(
  gate: ReadinessState,
  blockers: readonly Pick<ReadinessBlocker, "gates">[],
): string {
  return `${gate} is required (${blockersForGate(blockers, gate).length} blocker(s) remain)`;
}
